import { formatElement, elementClass } from '../../components/GameTerms';
import { renderMasteryIcon } from '../../components/ItemIcon';
import { getConfig } from './types';

export interface MasteriesState {
  categoryFilter: string;
}

export function createMasteriesState(): MasteriesState {
  return { categoryFilter: '' };
}

const CATEGORY_ORDER = ['primal', 'cosmic', 'martial', 'cunning'];

const CATEGORY_LABELS: Record<string, string> = {
  primal: 'Primal Elements',
  cosmic: 'Cosmic Arts',
  martial: 'Martial Disciplines',
  cunning: 'Cunning Tactics',
};

export function renderMasteries(content: HTMLElement, state: MasteriesState): void {
  const config = getConfig();
  if (!config) { content.innerHTML = '<p>Loading...</p>'; return; }

  const allMasteries = Object.values(config.masteries);
  const categories = [...new Set(allMasteries.map(m => m.category))];

  const filtered = state.categoryFilter
    ? allMasteries.filter(m => m.category === state.categoryFilter)
    : allMasteries;

  const categoryPills = categories.map(cat =>
    `<button class="codex-pill${state.categoryFilter === cat ? ' active' : ''}" data-mastery-cat="${cat}">${CATEGORY_LABELS[cat] ?? formatElement(cat)}</button>`
  ).join('');

  const renderCard = (m: typeof allMasteries[number]): string => {
    const statusBadge = m.status === 'nyi' ? '<span class="codex-nyi-badge">NYI</span>' : '';

    const rankLines: string[] = [];
    if (m.damageTag) {
      const el = m.damageTag;
      rankLines.push(`<span class="${elementClass(el)}">+1% ${formatElement(el)} damage</span>`);
    }
    const mAny = m as { rankBonus?: { label?: string } };
    if (mAny.rankBonus?.label) {
      rankLines.push(mAny.rankBonus.label);
    }
    const rankHtml = rankLines.length > 0
      ? `<div class="mastery-rank-bonuses">
           <div class="mastery-rank-bonus-label">Per Rank</div>
           ${rankLines.map(l => `<div class="mastery-rank-bonus-line">${l}</div>`).join('')}
         </div>`
      : '';

    return `
      <div class="mastery-card">
        <div class="mastery-card-header">
          ${renderMasteryIcon(m.id, 'md')}
          <div class="mastery-card-title">
            <div class="mastery-card-name">${m.name} ${statusBadge}</div>
            <div class="mastery-card-cat">${CATEGORY_LABELS[m.category] ?? m.category}</div>
          </div>
        </div>
        ${rankHtml}
        <div class="mastery-card-desc">${m.description}</div>
      </div>
    `;
  };

  // Group by category, sorted by canonical CATEGORY_ORDER. Within each category,
  // alpha sort with NYI sinking to the bottom.
  const groups = new Map<string, typeof filtered>();
  for (const m of filtered) {
    if (!groups.has(m.category)) groups.set(m.category, []);
    groups.get(m.category)!.push(m);
  }
  const sortedGroups = [...groups.entries()].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a[0]);
    const bi = CATEGORY_ORDER.indexOf(b[0]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  for (const [, list] of sortedGroups) {
    list.sort((a, b) => {
      const ad = a.status === 'nyi' ? 1 : 0;
      const bd = b.status === 'nyi' ? 1 : 0;
      if (ad !== bd) return ad - bd;
      return a.name.localeCompare(b.name);
    });
  }

  const groupHtml = sortedGroups.map(([cat, list]) => {
    const label = CATEGORY_LABELS[cat] ?? formatElement(cat);
    return `
      <div class="codex-family-group">
        <div class="codex-family-header">${label} <span class="codex-family-count">${list.length}</span></div>
        <div class="codex-mastery-grid">${list.map(renderCard).join('')}</div>
      </div>
    `;
  }).join('');

  content.innerHTML = `
    <h2 class="codex-section-title">Masteries</h2>
    <p class="codex-section-subtitle">${allMasteries.length} total</p>
    <div class="codex-filter-pills">
      <button class="codex-pill${!state.categoryFilter ? ' active' : ''}" data-mastery-cat="">All</button>
      ${categoryPills}
    </div>
    ${groupHtml || '<p class="codex-empty">No masteries match filters</p>'}
  `;

  content.querySelectorAll('[data-mastery-cat]').forEach(btn => {
    btn.addEventListener('click', () => {
      state.categoryFilter = (btn as HTMLElement).dataset.masteryCat || '';
      renderMasteries(content, state);
    });
  });
}
